import { useState, useEffect } from "react";
import {
  ChevronLeft,
  Eye,
  Edit,
  Trash,
  Check,
  X,
  Search,
  Loader,
  Calendar,
  Filter,
} from "lucide-react";
import blogService from "@/services/api/blog.service";

import CreateBlog from "./CreateBlog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const Posts = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [blogToEdit, setBlogToEdit] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    category: "",
    tag: "",
    startDate: "",
    endDate: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch blogs on component mount and when filters/pagination change
  useEffect(() => {
    fetchBlogs();
  }, [currentPage, filters]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await blogService.getPosts(currentPage, 12, {
        status: filters.status,
        search: filters.search,
        category: filters.category,
        tag: filters.tag,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      setBlogs(response.docs || []);
      setTotalPages(response.totalPages || 0);
      setTotalBlogs(response.totalDocs || 0);
    } catch (error) {
      console.error("Failed to fetch blogs:", error);
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleViewBlog = async (slug) => {
    try {
      setLoading(true);
      const blogDetail = await blogService.getPostBySlug(slug);
      setSelectedBlog(blogDetail);
    } catch (error) {
      console.error("Failed to fetch blog details:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDeletePost = async (postId) => {
    try {
 

      const loadingToast = toast.loading("Deleting post...");

      await blogService.deletePost(postId); // <-- Call your service

      toast.update(loadingToast, {
        render: "Post deleted successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // Update your blog list
      setBlogs((prevBlogs) => prevBlogs.filter((blog) => blog._id !== postId));
    } catch (error) {
      toast.error(error.message || "Failed to delete post");
    }
  };

  const handleBackToList = () => {
    setSelectedBlog(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };
  // const handleEditBlog = async (slug) => {
  //   try {
  //     setLoading(true);

  //     const blogDetail = await blogService.getPostBySlug(slug);

  //     setBlogToEdit(blogDetail);
  //     setSelectedBlog(null); // ⬅️ This hides the detail view
  //     setShowEditForm(true); // ⬅️ Then show the edit form
  //   } catch (error) {
  //     console.error("Failed to fetch blog details for editing:", error);
  //     toast.error("Failed to load blog for editing");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleEditBlog = async (slug) => {
    try {
      setLoading(true);
      const blogDetail = await blogService.getPostBySlug(slug);
      setBlogToEdit(blogDetail);
      setSelectedBlog(null);
      setShowEditForm(true);
    } catch (error) {
      console.error("Failed to fetch blog details for editing:", error);
      toast.error("Failed to load blog for editing");
    } finally {
      setLoading(false);
    }
  };
  const clearFilters = () => {
    setFilters({
      status: "",
      search: "",
      category: "",
      tag: "",
      startDate: "",
      endDate: "",
    });
  };

  // Function to convert binary image data to a display format
  const renderBlogImage = (blog) => {
    if (blog.featuredImage?.data) {
      // Convert binary data to base64
      const base64String = arrayBufferToBase64(blog.featuredImage.data);
      const contentType = blog.featuredImage.contentType || "image/jpeg";
      return (
        <img
          src={`data:${contentType};base64,${base64String}`}
          alt={blog.featuredImage.alt || blog.title}
          className="w-full h-full object-cover" // Changed from object-contain to object-cover
        />
      );
    }

    // Fallback image
    return (
      <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
        <span className="text-gray-500">No Image</span>
      </div>
    );
  };

  // Helper function to convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer) => {
    if (!buffer || buffer.type !== "Buffer" || !buffer.data) {
      return "";
    }

    let binary = "";
    const bytes = new Uint8Array(buffer.data);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  // Render status badge with appropriate color
  const renderStatusBadge = (status) => {
    const statusStyles = {
      published: "bg-green-100 text-green-800",
      draft: "bg-yellow-100 text-yellow-800",
      archived: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          statusStyles[status] || "bg-gray-100"
        }`}
      >
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
      </div>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="container mx-auto px-4 py-6">
        {selectedBlog ? (
          // Blog Detail View with improved layout
          <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto">
            <button
              onClick={handleBackToList}
              className="flex items-center text-blue-600 mb-6 hover:text-blue-800 transition-colors font-medium"
            >
              <ChevronLeft size={16} className="mr-1" />
              Back to all blogs
            </button>

            <div>
              {/* Header section with title, actions, status and date */}
              <div className="flex justify-between items-start mb-4">
                <h1 className="text-3xl font-bold text-gray-800">
                  {selectedBlog.title}
                </h1>
                <div className="flex space-x-2">
                  <button
                    className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                    onClick={() =>
                      selectedBlog && handleEditBlog(selectedBlog.slug)
                    }
                    disabled={!selectedBlog}
                    title={
                      !selectedBlog
                        ? "Select a blog to edit"
                        : "Edit selected blog"
                    }
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDeletePost(selectedBlog._id)}
                    className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-3 mb-6">
                {renderStatusBadge(selectedBlog.status)}
                <span className="text-sm text-gray-500">
                  {new Date(selectedBlog.createdAt).toLocaleDateString()}
                </span>
                <span className="text-sm text-gray-500 flex items-center">
                  <Eye size={14} className="mr-1" />
                  {selectedBlog.stats?.views || 0} views
                </span>
              </div>

              {/* Featured image with improved layout */}
              {selectedBlog.featuredImage?.data && (
                <div className="mb-8 w-full bg-gray-50 rounded-lg overflow-hidden">
                  <div className="w-full flex justify-center items-center p-2">
                    <img
                      src={`data:${
                        selectedBlog.featuredImage.contentType || "image/jpeg"
                      };base64,${arrayBufferToBase64(
                        selectedBlog.featuredImage.data
                      )}`}
                      alt={selectedBlog.featuredImage.alt || selectedBlog.title}
                      className="w-full max-h-80 object-contain rounded-md shadow-sm"
                    />
                  </div>
                  {selectedBlog.featuredImage.caption && (
                    <p className="text-sm text-gray-600 mt-2 mb-1 text-center w-full italic">
                      {selectedBlog.featuredImage.caption}
                    </p>
                  )}
                </div>
              )}

              {/* Categories and tags with improved styling */}
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedBlog.categories &&
                  selectedBlog.categories.map((category) => (
                    <span
                      key={category._id}
                      className="bg-purple-100 text-purple-800 text-xs px-3 py-1 rounded-full transition-colors hover:bg-purple-200"
                    >
                      {category.name}
                    </span>
                  ))}

                {selectedBlog.tags &&
                  selectedBlog.tags.map((tag) => (
                    <span
                      key={tag._id}
                      className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full transition-colors hover:bg-blue-100"
                    >
                      {tag.name}
                    </span>
                  ))}
              </div>

              {/* Author section with improved styling */}
              {selectedBlog.author && (
                <div className="flex items-center mb-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  {selectedBlog.author.profileImage ? (
                    <img
                      src={selectedBlog.author.profileImage}
                      alt={selectedBlog.author.name}
                      className="w-12 h-12 rounded-full mr-4 shadow-sm"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center mr-4 shadow-sm">
                      <span className="text-blue-700 font-semibold uppercase text-lg">
                        {selectedBlog.author.email?.charAt(0) || "A"}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-800">
                      {selectedBlog.author.name || "Author"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedBlog.author.email}
                    </p>
                  </div>
                </div>
              )}

              {/* Main content with improved styling */}
              <div className="prose max-w-none bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-8">
                <div
                  dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                />
              </div>

              {/* Comments section with improved styling */}
              {selectedBlog.comments && selectedBlog.comments.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <MessageSquare size={18} className="mr-2" />
                    Comments ({selectedBlog.comments.length})
                  </h2>
                  <div className="space-y-4">
                    {selectedBlog.comments.map((comment) => (
                      <div
                        key={comment._id}
                        className="bg-gray-50 p-4 rounded-lg border border-gray-100 hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between">
                          <p className="font-medium text-gray-800">
                            {comment.author ? comment.author.name : "Anonymous"}
                          </p>
                          <div className="flex items-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                comment.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : comment.status === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {comment.status}
                            </span>
                            <div className="ml-2 flex space-x-1">
                              <button className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors">
                                <Check size={14} />
                              </button>
                              <button className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors">
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-2">
                          {comment.content}
                        </p>
                        <p className="text-xs text-gray-500 mt-2 flex items-center">
                          <Calendar size={12} className="mr-1" />
                          {new Date(comment.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : showCreateForm ? (
          // Render CreateBlog component
          <>
            <button
              onClick={() => setShowCreateForm(false)}
              className="flex items-center text-blue-600 mb-4 hover:text-blue-800 transition-colors"
            >
              <ChevronLeft size={16} className="mr-1" />
              Back to all blogs
            </button>
            <CreateBlog
              onSuccess={() => {
                setShowCreateForm(false);
                fetchBlogs(); // Refresh blogs after successful creation
              }}
              onCancel={() => {
                setShowCreateForm(false); // ← triggers "back to blog" behavior
              }}
            />
          </>
        ) : showEditForm ? (
          // Add Edit Form rendering
          <>
            <button
              onClick={() => {
                setShowEditForm(false);
                setBlogToEdit(null);
              }}
              className="flex items-center text-blue-600 mb-4 hover:text-blue-800 transition-colors"
            >
              <ChevronLeft size={16} className="mr-1" />
              Back to all blogs
            </button>
            <CreateBlog
              isEditing={true}
              blogData={blogToEdit}
              onSuccess={() => {
                setShowEditForm(false);
                setBlogToEdit(null);
                fetchBlogs(); // Refresh blogs after successful update
              }}
              onCancel={() => {
                setShowEditForm(false);
                setBlogToEdit(null); // ← triggers "back to blog" behavior
              }}
            />
          </>
        ) : (
          // Blog List View
          <>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                  Blog Management
                </h1>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  onClick={() => setShowCreateForm(true)}
                >
                  <span className="mr-1">+</span> New Blog
                </button>
              </div>
              {/* Search bar */}
              <div className="flex mb-4">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    name="search"
                    placeholder="Search blogs..."
                    className="w-full border rounded-l-lg px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    value={filters.search}
                    onChange={handleFilterChange}
                  />
                  <Search
                    size={18}
                    className="absolute left-3 top-2.5 text-gray-400"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2 flex items-center border border-l-0 ${
                    showFilters
                      ? "bg-blue-50 text-blue-600"
                      : "bg-white text-gray-700"
                  }`}
                >
                  <Filter size={16} className="mr-1" />
                  Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-r-lg hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              </div>

              {/* Expanded filters */}
              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                      value={filters.status}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Statuses</option>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="startDate"
                        className="w-full border rounded-lg px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        value={filters.startDate}
                        onChange={handleFilterChange}
                      />
                      <Calendar
                        size={16}
                        className="absolute left-3 top-2.5 text-gray-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="endDate"
                        className="w-full border rounded-lg px-3 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-300"
                        value={filters.endDate}
                        onChange={handleFilterChange}
                      />
                      <Calendar
                        size={16}
                        className="absolute left-3 top-2.5 text-gray-400"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Blog cards */}
              {blogs.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-lg">
                  <div className="mb-4 text-gray-400">
                    <Search size={48} className="mx-auto" />
                  </div>
                  <p className="text-gray-500 text-lg">
                    No blogs found matching your filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="mt-3 px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {blogs.map((blog) => (
                    <div
                      key={blog._id}
                      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
                    >
                      <div className="relative h-48 w-full overflow-hidden">
                        {/* Removed bg-gray-100 to ensure full width */}
                        {renderBlogImage(blog)}
                        <div className="absolute top-2 right-2 z-10">
                          {renderStatusBadge(blog.status)}
                        </div>
                      </div>

                      <div className="p-4 flex flex-col flex-1">
                        <h2
                          className="font-bold text-lg line-clamp-2 mb-2 hover:text-blue-600 transition-colors cursor-pointer"
                          onClick={() => handleViewBlog(blog.slug)}
                        >
                          {blog.title}
                        </h2>

                        <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                          <span>
                            {new Date(blog.createdAt).toLocaleDateString()}
                          </span>
                          <span className="flex items-center">
                            <Eye size={12} className="mr-1" />
                            {blog.stats?.views || 0}
                          </span>
                        </div>

                        {blog.content && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {blog.content.replace(/<[^>]*>/g, "")}
                          </p>
                        )}

                        <div className="flex justify-between items-center mt-auto pt-3 border-t border-gray-100">
                          <div className="flex items-center">
                            {blog.author && (
                              <div className="flex items-center">
                                <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center mr-2">
                                  <span className="text-blue-700 text-xs font-semibold">
                                    {blog.author.email?.charAt(0) || "A"}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-700 truncate max-w-[80px]">
                                  {blog.author.email}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex space-x-1">
                            <button
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                              title="View Details"
                              onClick={() => handleViewBlog(blog.slug)}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                              title="Edit Blog"
                              onClick={() => handleEditBlog(blog.slug)}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeletePost(blog._id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                              title="Delete Blog"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col items-center mt-8">
                  <div className="inline-flex rounded-md shadow mb-4">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="px-3 py-2 rounded-l-md bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        return (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        );
                      })
                      .map((page, index, array) => {
                        if (index > 0 && array[index - 1] !== page - 1) {
                          return (
                            <span
                              key={`ellipsis-${page}`}
                              className="px-3 py-2 bg-white border border-gray-300 text-sm font-medium text-gray-700"
                            >
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 border ${
                              currentPage === page
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 rounded-r-md bg-white border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>

                  {/* Add pagination info */}
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages} | Total {totalBlogs}{" "}
                    blogs
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Posts;
